import React, { useEffect, useMemo, useState } from 'react';
import { type DateRange } from 'react-day-picker';
import { FormattedMessage, useIntl } from 'react-intl';

import CalendarIcon from '@/assets/icons/calendar.svg';
import ChevronDownIcon from '@/assets/icons/chevron-down.svg';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { formatShortDate } from '@/utils/date-util';

import { Typography } from './typography';

interface DateRangePickerProps {
  selectedDate: DateRange;
  setSelectedDate: React.Dispatch<React.SetStateAction<DateRange>>;
  defaultRange: DateRange;
}

export function DateRangePicker({
  selectedDate,
  setSelectedDate,
  defaultRange,
}: DateRangePickerProps) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<DateRange>(selectedDate);

  useEffect(() => {
    setDate(selectedDate);
  }, [selectedDate]);

  const buttonText = useMemo(() => {
    const isDefaultRange =
      date.from === defaultRange.from && date.to === defaultRange.to;

    if (isDefaultRange)
      return intl.formatMessage({ defaultMessage: 'Last 15 days' });

    if (date.from && date.to) {
      return `${formatShortDate(date.from)} - ${formatShortDate(date.to)}`;
    }

    return intl.formatMessage({ defaultMessage: 'Pick a range' });
  }, [date, defaultRange, intl]);

  const handleApply = () => {
    setSelectedDate(date);
    setOpen(false);
  };

  const handleClear = () => {
    setDate(selectedDate);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id="date"
          variant="ghost"
          className="font-normal"
          onClick={() => setOpen(true)}
        >
          <img src={CalendarIcon} alt="" aria-hidden="true" />
          <span>{buttonText}</span>
          <img src={ChevronDownIcon} alt="" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          autoFocus
          mode="range"
          required
          selected={date}
          onSelect={setDate}
          numberOfMonths={2}
        />
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={handleClear}>
            <Typography variant="body2" className="text-fx-primary">
              <FormattedMessage defaultMessage="Clear" />
            </Typography>
          </Button>
          <Button variant="ghost" size="sm" onClick={handleApply}>
            <Typography variant="body2" className="text-fx-primary">
              <FormattedMessage defaultMessage="Apply" />
            </Typography>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
